/**
 * RelatedInvestigations — Sprint 12 Phase B chip panel.
 *
 * Renders the top-k cosine-similar past episodes returned by the
 * `memory_context` SSE event as a horizontal row of chip cards. Each card
 * shows:
 *
 *   - date window (from → to UTC)
 *   - services involved (one or more chips)
 *   - short causal-chain (symptom / proximate / trigger / root)
 *   - similarity score (badge)
 *   - feedback thumb if present
 *
 * Click-through navigates to the referenced investigation or analysis.
 *
 * Empty state: render nothing at all (no "no related investigations"
 * placeholder) — see plan §B.7. This keeps the live-narrative pane the
 * focal point when the corpus has nothing to say.
 */

import { ArrowOutward, ThumbDownAltOutlined, ThumbUpAltOutlined } from '@mui/icons-material';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { MemoryEpisode } from '@/types/investigation';

export interface RelatedInvestigationsProps {
  episodes: MemoryEpisode[] | null;
}

export function RelatedInvestigations({ episodes }: RelatedInvestigationsProps): ReactElement | null {
  if (!episodes || episodes.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="related-investigations"
      sx={{
        mb: 3,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          color: 'text.primary',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.8125rem',
          fontWeight: 600,
          letterSpacing: 0.5,
          mb: 1.5,
          textTransform: 'uppercase',
        }}
      >
        Related past investigations
      </Typography>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          overflowX: 'auto',
          pb: 1,
          // Hide native scrollbar visually but keep keyboard scroll.
          scrollbarWidth: 'thin',
        }}
      >
        {episodes.map((episode) => (
          <EpisodeCard key={episode.id} episode={episode} />
        ))}
      </Stack>
    </Box>
  );
}

interface EpisodeCardProps {
  episode: MemoryEpisode;
}

function EpisodeCard({ episode }: EpisodeCardProps): ReactElement {
  const detailPath =
    episode.source_type === 'investigation'
      ? `/investigate/${episode.source_id}`
      : `/issues?analysis=${episode.source_id}`;

  const window = formatWindow(episode.anchor_from, episode.anchor_to);
  const causal = formatCausal(episode.causal_chain);
  const similarityPct = Math.round(episode.similarity * 100);

  return (
    <Box
      component={RouterLink}
      to={detailPath}
      aria-label={`Open related ${episode.source_type} (similarity ${similarityPct}%)`}
      sx={{
        flexShrink: 0,
        minWidth: 280,
        maxWidth: 360,
        minHeight: 44,
        textDecoration: 'none',
        color: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        bgcolor: 'background.paper',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover, &:focus-visible': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontFamily: '"JetBrains Mono", monospace',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {window}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {episode.feedback_thumb === 1 && (
            <Tooltip title="Marked helpful">
              <ThumbUpAltOutlined sx={{ fontSize: 14, color: 'success.main' }} />
            </Tooltip>
          )}
          {episode.feedback_thumb === -1 && (
            <Tooltip title="Marked not helpful">
              <ThumbDownAltOutlined sx={{ fontSize: 14, color: 'error.main' }} />
            </Tooltip>
          )}
          <Chip
            label={`${similarityPct}%`}
            size="small"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontVariantNumeric: 'tabular-nums',
              height: 18,
              '& .MuiChip-label': { px: 0.75, fontSize: '0.6875rem' },
            }}
          />
          <ArrowOutward sx={{ fontSize: 14, color: 'text.secondary' }} aria-hidden />
        </Stack>
      </Stack>

      {episode.services.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {episode.services.slice(0, 3).map((svc) => (
            <Chip
              key={svc}
              label={svc}
              size="small"
              variant="outlined"
              sx={{
                height: 18,
                '& .MuiChip-label': { px: 0.75, fontSize: '0.6875rem' },
              }}
            />
          ))}
        </Stack>
      )}

      <Typography
        variant="body2"
        sx={{
          fontSize: '0.8125rem',
          lineHeight: 1.4,
          color: 'text.primary',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {causal || episode.narrative_preview || '(no narrative captured)'}
      </Typography>
    </Box>
  );
}

function formatWindow(fromStr: string | null, toStr: string | null): string {
  const formatPart = (s: string | null): string => {
    if (!s) return '?';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '?';
    // Show YYYY-MM-DD HH:mm (UTC).
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const hour = String(d.getUTCHours()).padStart(2, '0');
    const minute = String(d.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}`;
  };
  if (!fromStr && !toStr) return 'window unknown';
  return `${formatPart(fromStr)} → ${formatPart(toStr)} UTC`;
}

function formatCausal(chain: MemoryEpisode['causal_chain']): string {
  const parts: string[] = [];
  if (chain.root_cause) {
    parts.push(`root=${chain.root_cause}`);
  } else if (chain.trigger) {
    parts.push(`trigger=${chain.trigger}`);
  }
  if (chain.symptom) {
    parts.unshift(`symptom=${chain.symptom}`);
  }
  return parts.join(' · ');
}
